import {
  extractHomeContentData,
  HomeLayoutProps,
} from "@app/layouts/Home/Elements/utils";
import { DataSource } from "@pub/config";
import { Typography } from "@sphtech/dmg-design-system";
import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import { carouselLeftIcon, carouselRightIcon } from "../icons/commonIcons";
import styles from "./trendingcarousel.module.css";
import TrendingCarouselCard from "./TrendingCarouselCard";

const { Heading } = Typography;

const IS_VRT = import.meta.env.VITE_DISABLE_EMBEDS === "true";
const ITEM_WIDTH = 295;
const GAP = 50;
const SCROLL_AMOUNT = ITEM_WIDTH + GAP;
const SLIDE_INTERVAL_TIMING = 5_000;

type Props = {
  data: HomeLayoutProps["data"];
  dataSource: DataSource;
  alternateBg?: boolean;
};

const TrendingCarousel = ({ data, dataSource }: Props) => {
  const carouselData = extractHomeContentData(data, dataSource);
  const teasers = carouselData?.items ?? [];

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleClickScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
        behavior: "smooth",
      });
    }
  };

  // Synchronises any scrolling motion to the activeIndex
  const handleTouchScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      // Snaps to the index of the card that is currently most visible
      const newIndex = Math.round(scrollLeft / SCROLL_AMOUNT);
      if (
        newIndex !== activeIndex &&
        newIndex >= 0 &&
        newIndex < teasers.length
      ) {
        setActiveIndex(newIndex);
      }
    }
  };

  const handleScrollToItem = useCallback((index: number) => {
    if (scrollContainerRef.current) {
      const targetScroll = index * SCROLL_AMOUNT;
      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    // To prevent autoplay
    if (IS_VRT || isPaused || teasers.length <= 1) return;

    const slideInterval = setInterval(() => {
      // Loops around once it reachers the end of the teasers
      const nextIndex = (activeIndex + 1) % teasers.length;
      handleScrollToItem(nextIndex);
    }, SLIDE_INTERVAL_TIMING);

    return () => {
      clearInterval(slideInterval);
    };
  }, [activeIndex, teasers.length, isPaused, handleScrollToItem]);

  if (!carouselData || carouselData.items.length === 0) {
    return null;
  }

  return (
    <div className={styles.trendingContainer}>
      <div className={styles.headingTitle}>
        <Heading.SubSectionTitle nameZH="热点">
          Trending
        </Heading.SubSectionTitle>
      </div>

      <div className={styles.outerScrollContainer}>
        {activeIndex > 0 && (
          <button
            type="button"
            onClick={() => {
              handleClickScroll("left");
            }}
            className={styles.btn}
          >
            {carouselLeftIcon}
          </button>
        )}

        {activeIndex < teasers.length - 1 && (
          <button
            type="button"
            onClick={() => {
              handleClickScroll("right");
            }}
            className={cx(styles.btn, styles.btnRight)}
          >
            {carouselRightIcon}
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={handleTouchScroll}
          className={styles.innerScrollContainer}
          onMouseEnter={() => {
            setIsPaused(true);
          }}
          onMouseLeave={() => {
            setIsPaused(false);
          }}
        >
          {teasers.map((contentBlockItem, index) => {
            return (
              <TrendingCarouselCard
                key={index}
                contentBlockItem={contentBlockItem}
              />
            );
          })}
        </div>

        <section className={styles.controller}>
          {teasers.map((_, index) => {
            // Calculates which item is "pending" for the timer
            const nextActiveIndex = (activeIndex + 1) % teasers.length;
            const isNext = index === nextActiveIndex;

            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  handleScrollToItem(index);
                }}
                style={{
                  animationDuration: `${SLIDE_INTERVAL_TIMING}ms`,
                }}
                className={cx(
                  styles.controllerBtn,
                  index <= activeIndex && styles.active,
                  isNext && styles.pieTimer,
                  isPaused && styles.paused,
                )}
              />
            );
          })}
        </section>
      </div>
    </div>
  );
};

export default TrendingCarousel;
